// WhatsNewSeen — 「新機能の紹介」の既読 ID を端末の localStorage に記録する。
//
// 方針 (docs/_wip/whats-new-2026-10-15.md): 消えても「未読の印がもう一度付く」だけで
// 実害はない。storage 不可 / 壊れた値は例外にせず「常に未読扱い」で動作を続ける。
// 流儀は helpButtonSeen.ts と同じ (StorageLike 抽象 + sentinel probe、test で注入)。

export const WHATS_NEW_SEEN_KEY = 'oekaki:whats-new-seen';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface WhatsNewSeenStore {
  /** 既読 ID の集合。不在 / 壊れた値 / storage 不可 → 空。 */
  readSeenIds(): ReadonlySet<string>;
  /** 渡した ID を既読に加える (既存の既読は保持)。storage 不可なら no-op。 */
  markSeen(ids: ReadonlyArray<string>): void;
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:whats-new-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    return got === '1' ? ls : null;
  } catch {
    return null;
  }
};

export interface CreateWhatsNewSeenStoreOptions {
  /** 既定は localStorage を probe。`null` で no-op (常に未読) を強制。 */
  storage?: StorageLike | null;
}

const parseIds = (raw: string | null): Set<string> => {
  if (raw === null) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
};

export const createWhatsNewSeenStore = (
  opts: CreateWhatsNewSeenStoreOptions = {},
): WhatsNewSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  const read = (): Set<string> => {
    if (storage === null) return new Set();
    try {
      return parseIds(storage.getItem(WHATS_NEW_SEEN_KEY));
    } catch {
      return new Set();
    }
  };

  return {
    readSeenIds: read,
    markSeen(ids) {
      if (storage === null) return;
      try {
        const next = read();
        for (const id of ids) next.add(id);
        storage.setItem(WHATS_NEW_SEEN_KEY, JSON.stringify([...next]));
      } catch {
        // QuotaExceeded / SecurityError 等 — 未読の印が出直すだけで害はない。
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(WHATS_NEW_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
